import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { Home, FileText, DollarSign, Image as ImageIcon, Newspaper, Library as LibraryIcon, LogOut, User, Bot, MessageSquare, Phone, ExternalLink, Archive, Camera, Loader2, CalendarDays } from 'lucide-react';
import { toast } from 'sonner';
import useAuthStore from '../store/authStore';
import api, { getMediaBlobUrl } from '../utils/api';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_0a775788-3b72-4cac-99b0-2ae8be67d728/artifacts/5ne6bwob_Alhaj_Halfan_Swai_Logo.png';

// Contact Numbers
const TECHNICAL_SUPPORT = '+255 753 541 330';
const AHSGA_WHATSAPP_1 = '255755646776';
const AHSGA_WHATSAPP_1_DISPLAY = '+255 755 646 776';
const AHSGA_WHATSAPP_2 = '255713939377';
const AHSGA_WHATSAPP_2_DISPLAY = '+255 713 939 377';

const MemberLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, updateUser } = useAuthStore();
  const fileInputRef = useRef(null);
  const [profileImageUrl, setProfileImageUrl] = useState(null);
  const [uploadingPicture, setUploadingPicture] = useState(false);

  const menuItems = [
    { path: '/member', icon: Home, label: 'Dashboard' },
    { path: '/member/ai-assistant', icon: Bot, label: 'AI Assistant', highlight: true },
    { path: '/member/meetings', icon: Archive, label: 'Meeting Archive' },
    { path: '/member/agenda', icon: CalendarDays, label: 'Agenda' },
    { path: '/member/minutes', icon: FileText, label: 'Minutes' },
    { path: '/member/financial-statements', icon: DollarSign, label: 'Financial' },
    { path: '/member/gallery', icon: ImageIcon, label: 'Gallery' },
    { path: '/member/news', icon: Newspaper, label: 'News' },
    { path: '/member/library', icon: LibraryIcon, label: 'Library' },
  ];

  // Load profile picture
  useEffect(() => {
    const loadProfilePicture = async () => {
      if (user?.profile_picture) {
        try {
          const url = await getMediaBlobUrl(user.profile_picture);
          setProfileImageUrl(url);
        } catch (error) {
          console.error('Failed to load profile picture');
        }
      }
    };
    loadProfilePicture();
    return () => {
      if (profileImageUrl) window.URL.revokeObjectURL(profileImageUrl);
    };
  }, [user?.profile_picture]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const openWhatsApp1 = () => {
    window.open(`https://wa.me/${AHSGA_WHATSAPP_1}`, '_blank');
  };

  const openWhatsApp2 = () => {
    window.open(`https://wa.me/${AHSGA_WHATSAPP_2}`, '_blank');
  };

  const handleProfilePictureClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Please upload a JPG, PNG, or WEBP image');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setUploadingPicture(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post('/auth/profile-picture', formData);
      updateUser({ profile_picture: response.data.profile_picture });
      
      const url = await getMediaBlobUrl(response.data.profile_picture);
      setProfileImageUrl(url);
      
      toast.success('Profile picture updated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload profile picture');
    } finally {
      setUploadingPicture(false);
    }
  };

  return (
    <div className="flex h-screen bg-slate-50">
      <aside className="w-72 bg-white border-r border-slate-200 flex flex-col">
        {/* Integrated Brand Header */}
        <div className="relative overflow-hidden">
          {/* Background Pattern with Logo */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute -top-10 -right-10 w-40 h-40">
              <img src={LOGO_URL} alt="" className="w-full h-full object-contain" />
            </div>
            <div className="absolute -bottom-5 -left-5 w-24 h-24">
              <img src={LOGO_URL} alt="" className="w-full h-full object-contain opacity-50" />
            </div>
          </div>
          
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-orange-50/30" />
          
          {/* Content */}
          <div className="relative p-6 border-b border-slate-100">
            <div className="flex items-center space-x-4">
              {/* Logo Container with Subtle Shadow */}
              <div className="relative">
                <div className="absolute inset-0 bg-orange-200/30 rounded-2xl blur-lg" />
                <div className="relative bg-white p-2 rounded-2xl border border-slate-100 shadow-lg shadow-slate-200/50">
                  <img src={LOGO_URL} alt="AHSGA Logo" className="w-14 h-14 object-contain" />
                </div>
              </div>
              
              {/* Brand Text */}
              <div className="flex-1">
                <h1 className="text-3xl font-extrabold tracking-tight bg-gradient-to-r from-slate-900 via-slate-700 to-slate-800 bg-clip-text text-transparent" style={{ fontFamily: "'Poppins', sans-serif" }}>
                  AHSGA
                </h1>
                <div className="flex items-center space-x-2 mt-1">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
                  <p className="text-xs text-slate-500 font-medium uppercase tracking-widest">Member Portal</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-4 overflow-y-auto">
          <div className="space-y-1">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                    isActive
                      ? 'bg-gradient-to-r from-slate-900 to-slate-800 text-white shadow-lg shadow-slate-300/30'
                      : item.highlight
                        ? 'text-blue-600 hover:bg-blue-50 bg-blue-50/50 border border-blue-100'
                        : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                  {item.highlight && !isActive && (
                    <span className="ml-auto text-[10px] bg-blue-500 text-white px-1.5 py-0.5 rounded-full font-semibold">NEW</span>
                  )}
                </Link>
              );
            })}
          </div>

          {/* Contact Information */}
          <div className="mt-4 pt-4 border-t border-slate-100">
            <div className="px-4 py-2 mb-3">
              <p className="text-xs text-slate-500 mb-1">For technical issues:</p>
              <div className="flex items-center space-x-2 bg-slate-50 rounded-xl px-3 py-2 border border-slate-100">
                <Phone className="w-4 h-4 text-slate-400" />
                <span className="text-sm font-mono font-medium text-slate-700">{TECHNICAL_SUPPORT}</span>
              </div>
            </div>
            
            <div className="px-4 py-2">
              <p className="text-xs text-slate-500 mb-2">For information about AHSGA (WhatsApp):</p>
              <div className="space-y-2">
                <button
                  onClick={openWhatsApp1}
                  className="w-full flex items-center space-x-2 px-3 py-2.5 rounded-xl bg-gradient-to-r from-green-50 to-emerald-50 text-green-700 hover:from-green-100 hover:to-emerald-100 transition-all border border-green-100"
                  data-testid="whatsapp-1-btn"
                >
                  <MessageSquare className="w-4 h-4" />
                  <span className="text-sm font-medium">{AHSGA_WHATSAPP_1_DISPLAY}</span>
                </button>
                <button
                  onClick={openWhatsApp2}
                  className="w-full flex items-center space-x-2 px-3 py-2.5 rounded-xl bg-gradient-to-r from-green-50 to-emerald-50 text-green-700 hover:from-green-100 hover:to-emerald-100 transition-all border border-green-100"
                  data-testid="whatsapp-2-btn"
                >
                  <MessageSquare className="w-4 h-4" />
                  <span className="text-sm font-medium">{AHSGA_WHATSAPP_2_DISPLAY}</span>
                </button>
              </div>
            </div>
            
            <a
              href="https://baobaborphanage.org"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full flex items-center space-x-3 px-4 py-3 rounded-xl text-slate-500 hover:bg-slate-50 hover:text-slate-700 transition-all mt-2"
            >
              <ExternalLink className="w-5 h-5" />
              <span className="font-medium text-sm">Baobab Orphanage Centre</span>
            </a>
          </div>
        </nav>

        {/* User Profile Section */}
        <div className="p-4 border-t border-slate-100 bg-slate-50/50">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-3">
              <div 
                className="relative group cursor-pointer"
                onClick={handleProfilePictureClick}
                title="Click to change profile picture"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-slate-200 to-slate-300 rounded-xl flex items-center justify-center overflow-hidden ring-2 ring-slate-200/50 shadow-md">
                  {uploadingPicture ? (
                    <Loader2 className="w-5 h-5 text-slate-500 animate-spin" />
                  ) : profileImageUrl ? (
                    <img src={profileImageUrl} alt="Profile" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-lg font-bold text-slate-600">{user?.full_name?.charAt(0)}</span>
                  )}
                </div>
                <div className="absolute inset-0 bg-black/50 rounded-xl flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <Camera className="w-5 h-5 text-white" />
                </div>
                <input 
                  ref={fileInputRef}
                  type="file" 
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={handleFileChange}
                />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-900">{user?.full_name}</p>
                <p className="text-xs text-emerald-600">Member</p>
              </div>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center space-x-2 text-slate-600 hover:text-slate-900 transition-colors p-2.5 rounded-xl hover:bg-slate-100 border border-slate-200"
          >
            <LogOut className="w-4 h-4" />
            <span className="text-sm font-medium">Logout</span>
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-y-auto flex flex-col">
        <div className="watermark-container flex-1">
          <div className="watermark-content max-w-7xl mx-auto p-6 md:p-8">
            {children}
          </div>
        </div>
        
        <footer className="portal-footer">
          <a 
            href="https://baobaborphanage.org" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-sm"
          >
            <ExternalLink className="w-4 h-4" />
            Baobab Orphanage Centre
          </a>
          <p className="text-xs text-slate-500 mt-1">© 2026 AHSGA. All rights reserved.</p>
        </footer>
      </main>
    </div>
  );
};

export default MemberLayout;
